import { memo } from "react";

import { Box } from "@mui/material";

import { ConditionalLink } from "../../buttons";
import { OutlinedCard, Row } from "../../containers";
import { Checkbox } from "../../input";
import { useSelectionContext } from "../contexts";
import { TableColumn, TableRow } from "../types";
import { CardStatRow } from "./cardCommon";

interface PositionCardProps<TPosition> {
    row?: TableRow<TPosition>;
    index?: number;
    headerColumn: TableColumn<TPosition> | undefined;
    primaryColumn: TableColumn<TPosition> | undefined;
    statColumns: TableColumn<TPosition>[];
    actionColumn: TableColumn<TPosition> | undefined;
    navigatePath?: string | undefined;
    selecting?: boolean;
    loading?: boolean;
}

function PositionCardImpl<TPosition>({
    row,
    index = 0,
    headerColumn,
    primaryColumn,
    statColumns,
    actionColumn,
    navigatePath,
    selecting,
    loading
}: PositionCardProps<TPosition>) {
    const selection = useSelectionContext();

    if (loading || !row) {
        return (
            <OutlinedCard padding={2} spacing={1.5}>
                <Row loading />
                <CardStatRow columns={statColumns} loading />
            </OutlinedCard>
        );
    }

    const isSelected = !!selection && selection.isSelected(row.data);

    return (
        <Box
            onClick={selecting ? () => selection?.toggle(row.data) : undefined}
            sx={{ cursor: selecting ? "pointer" : undefined }}
        >
            <ConditionalLink to={selecting ? undefined : navigatePath}>
                <OutlinedCard padding={2} spacing={1.5} hoverable>
                    <Row spaceBetween sx={{ alignItems: "flex-start", gap: 1 }}>
                        <Row sx={{ alignItems: "center", gap: 1, minWidth: 0 }}>
                            {selecting && (
                                <span onClick={(e) => e.stopPropagation()}>
                                    <Checkbox checked={isSelected} onChange={() => selection?.toggle(row.data)} />
                                </span>
                            )}
                            {headerColumn?.render(row, index)}
                        </Row>
                        <Row sx={{ alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                            {primaryColumn?.render(row, index)}
                            {actionColumn && !selecting && (
                                <span onClick={(e) => e.stopPropagation()}>{actionColumn.render(row, index)}</span>
                            )}
                        </Row>
                    </Row>
                    <CardStatRow columns={statColumns} row={row} index={index} />
                </OutlinedCard>
            </ConditionalLink>
        </Box>
    );
}

export const PositionCard = memo(PositionCardImpl) as <TPosition>(props: PositionCardProps<TPosition>) => JSX.Element;
